import { MainSection } from 'src/components/design-system/struct/MainSection';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { AvatarWithDisplayValueColumn } from 'src/components/DataTable';
import React, { FC, ReactElement } from 'react';
import { getUserFullname } from 'src/utils/getFullname';
import { Helmet } from 'react-helmet-async';
import { ConfigProvider, Dropdown, Empty, Table } from 'antd';
import { EyeOutlined, MoreOutlined, UserOutlined } from '@ant-design/icons';
import { useGetBranchDebtsQuery } from 'src/api/endpoints/students-debts';
import { sortBy } from 'lodash';

export type StudentTotalDebt = {
    student: {
        id: number;
        userId: number;
        contract: string;

        phone: string | null;

        firstname: string | null;
        surname: string | null;
        patronymic: string | null;
    };
    totalDebt: string;
};

export const BranchDebtsPage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
): ReactElement => {
    const { data: studentTotalDebts, isLoading: isStudentTotalDebtsLoading } =
        useGetBranchDebtsQuery({
            params: { branchId: props.currentBranchId },
        });

    return (
        <StudentLayout auth={props}>
            <Helmet
                title={`Задолженности · ${props.currentBranch?.name} · Личный кабинет`}
            />
            <MainSection title={'Задолженности'}>
                <div className={'table'} style={{ width: '100%' }}>
                    <ConfigProvider
                        renderEmpty={() => {
                            return (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={'Задолженности отсутствуют'}
                                />
                            );
                        }}
                    >
                        <Table<StudentTotalDebt>
                            tableLayout={'fixed'}
                            dataSource={sortBy(studentTotalDebts, 'id')}
                            loading={isStudentTotalDebtsLoading}
                            rowKey={s => s.student.id}
                            pagination={false}
                        >
                            <Table.Column<StudentTotalDebt>
                                width={'40%'}
                                title={'Студент'}
                                render={(_, { student }) => (
                                    <AvatarWithDisplayValueColumn
                                        data={{
                                            userId: student.userId,
                                            displayValue: getUserFullname({
                                                ...student,
                                                name: student.firstname,
                                            }),
                                            description: student.contract,
                                        }}
                                        link={`/b/${props.currentBranchId}/students/${student.id}`}
                                    />
                                )}
                            />

                            <Table.Column<StudentTotalDebt>
                                width={'auto'}
                                title={'Общая сумма задолженности'}
                                render={(
                                    _,
                                    { totalDebt }: StudentTotalDebt
                                ) => {
                                    return (
                                        <div
                                            className={'student-total-debt'}
                                            style={{
                                                fontSize: '16px',
                                                color: '#eb4247',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {Number(totalDebt).toLocaleString(
                                                'ru-RU'
                                            ) + ' ₽'}
                                        </div>
                                    );
                                }}
                            />

                            <Table.Column<StudentTotalDebt>
                                width={'auto'}
                                title={'Телефон'}
                                render={(_, { student }: StudentTotalDebt) => {
                                    if (!student.phone) {
                                        return 'Не указан';
                                    }
                                    return student.phone;
                                }}
                            />

                            <Table.Column<StudentTotalDebt>
                                width={'50px'}
                                align={'end'}
                                render={(record: StudentTotalDebt) => (
                                    <Dropdown
                                        overlayClassName={'actions-dropdown'}
                                        arrow={false}
                                        placement={'bottomRight'}
                                        trigger={['click']}
                                        menu={{
                                            onClick: ({ key }) => {
                                                if (key === 'student') {
                                                    window.location.href = `/b/${props.currentBranchId}/students/${record.student.id}`;
                                                } else if (
                                                    key === 'student-debts'
                                                ) {
                                                    window.location.href = `/b/${props.currentBranchId}/students/${record.student.id}/debts`;
                                                }
                                            },
                                            items: [
                                                {
                                                    key: '1',
                                                    label: 'Действия',
                                                    disabled: true,
                                                },
                                                {
                                                    key: 'student',
                                                    label: 'Перейти к студенту',
                                                    icon: <UserOutlined />,
                                                },
                                                {
                                                    type: 'divider',
                                                },
                                                {
                                                    key: '2',
                                                    label: 'Задолженности',
                                                    disabled: true,
                                                },
                                                {
                                                    key: 'student-debts',
                                                    label: 'Открыть',
                                                    icon: <EyeOutlined />,
                                                },
                                            ],
                                        }}
                                    >
                                        <MoreOutlined />
                                    </Dropdown>
                                )}
                            />
                        </Table>
                    </ConfigProvider>
                </div>
            </MainSection>
        </StudentLayout>
    );
};
